import React, { useRef, useEffect } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  Code2,
  ShieldCheck,
  Layers,
  Palette,
  Wrench,
  CheckCircle2,
  Link as LinkIcon,
  Monitor,
  Phone,
  Send,
  RotateCw,
  Lock,
  Globe,
  Zap,
  ExternalLink,
} from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import ProductPageLayout from '../components/ProductPageLayout';

const HERO_VIDEO = 'https://customer-assets.emergentagent.com/job_3f9e40e3-cca9-4507-83e7-20ced098354a/artifacts/7ld93pyx_ECOMMERCE%20HERO%20SECTION%20VIDEO.mp4';
const BACKDROP_IMAGE = 'https://customer-assets.emergentagent.com/job_3f9e40e3-cca9-4507-83e7-20ced098354a/artifacts/mr1utx79_ECOMMERCE%20BACKDROP.png';

/* ─────────────────────────────────────── HERO ─────────────────────────────────────── */
/* No video here — a single fixed video sits behind the entire page (body), this hero
   simply adds a transparent scrim layer + the hero content on top of that video. */
const EcommerceHero = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const fromHome = ['/', 'footer'].includes(location.state?.from);
  const fromProductNav = location.state?.from === 'product-pagination';
  const productNavReturnTo = location.state?.returnTo;
  const fromTopNav = location.state?.from === 'top-nav';

  return (
    <section className="relative overflow-hidden" data-testid="ecommerce-hero">
      {/* Left-heavy dark scrim — lightened to match the other product pages while keeping left-side text legible */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'linear-gradient(to right, rgba(8,14,24,0.62) 0%, rgba(8,14,24,0.42) 35%, rgba(8,14,24,0.15) 60%, rgba(8,14,24,0.04) 85%, rgba(8,14,24,0) 100%)',
        }}
      />
      <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-b from-transparent to-[#0a1220]/20 pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-20 sm:pt-28 sm:pb-24 lg:pt-28 lg:pb-24 min-h-[75vh] flex flex-col">
        <button
          onClick={() => {
            if (fromTopNav) navigate('/');
            else if (fromProductNav && productNavReturnTo) navigate(productNavReturnTo);
            else if (fromHome) navigate(-1);
            else navigate('/products');
          }}
          className="self-start flex items-center gap-2 text-white/85 hover:text-white font-medium mb-14 sm:mb-16 transition-colors duration-200"
          data-testid="product-back-button"
        >
          <ArrowLeft size={18} />
          {fromTopNav ? 'Back to Home' : (fromHome || fromProductNav) ? 'Back' : 'Back to Products'}
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center flex-1">
          <div className="lg:col-span-7">
            <div
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#ef5b25]/20 border border-[#ef5b25]/50 text-[#ffcba6] text-[11px] font-bold uppercase tracking-[0.18em] mb-6 backdrop-blur-sm"
              data-testid="ecommerce-eyebrow"
            >
              <Globe size={12} />
              Online Payments
            </div>

            <h1
              className="text-5xl sm:text-6xl lg:text-[88px] font-bold text-white leading-[1.05] tracking-tight mb-6"
              style={{ textShadow: '0 6px 28px rgba(0,0,0,0.55)' }}
              data-testid="product-title"
            >
              eCommerce<br />Solutions
            </h1>

            <p
              className="text-lg sm:text-xl lg:text-2xl text-white/95 leading-relaxed max-w-2xl mb-10 font-medium"
              style={{ textShadow: '0 3px 14px rgba(0,0,0,0.55)' }}
            >
              Flexible online payment integration<br />
              designed for businesses of all sizes.
            </p>

            <Link
              to="/get-started-ecommerce"
              onClick={() => window.__resetSourceScroll?.()}
              className="group inline-flex items-center gap-2.5 px-7 py-3.5 bg-[#1F4E79] text-white font-bold rounded-lg hover:bg-[#163a5f] hover:gap-3.5 transition-all duration-200 shadow-xl hover:shadow-2xl"
              data-testid="ecommerce-cta-button"
            >
              Get Started Here</Link>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ─────────────────────────────────────── INTEGRATION CARD ─────────────────────────────────────── */
const IntegrationCard = ({ eyebrow, title, subtitle, intro, listTitle, items, bestFor, docLink, docState, docLabel, accent, icon: Icon, testId }) => (
  <div
    className="flex flex-col h-full bg-white/95 backdrop-blur-sm rounded-xl shadow-xl border border-white/40 overflow-hidden"
    data-testid={testId}
  >
    <div className={`bg-gradient-to-br ${accent} p-6 sm:p-8`}>
      <p className="text-[11px] font-bold text-white/90 uppercase tracking-[0.2em] mb-2">{eyebrow}</p>
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 mt-1">
          <Icon size={28} className="text-white" strokeWidth={1.75} />
        </div>
        <div>
          <h3 className="text-2xl sm:text-3xl font-bold text-white leading-tight">{title}</h3>
          <p className="text-white/90 mt-2 text-sm sm:text-base">{subtitle}</p>
        </div>
      </div>
    </div>

    <div className="p-6 sm:p-8 flex flex-col flex-1">
      <p className="text-gray-700 text-sm sm:text-base leading-relaxed mb-5">{intro}</p>

      <p className="text-[11px] font-bold text-[#1F4E79] uppercase tracking-[0.15em] mb-3">{listTitle}</p>
      <ul className="space-y-2.5 mb-6">
        {items.map((item, i) => (
          <li key={i} className="flex items-start gap-2.5">
            <CheckCircle2 size={16} className="text-[#1F4E79] flex-shrink-0 mt-0.5" />
            <span className="text-gray-800 text-[13.5px] leading-relaxed">{item}</span>
          </li>
        ))}
      </ul>

      <div className="mt-auto pt-2">
        <div className="rounded-md border border-amber-200 bg-amber-50 text-amber-900 p-3.5 text-[13px] mb-5">
          <strong>Best For:</strong> {bestFor}
        </div>
        <Link
          to={docLink}
          state={docState}
          className="group inline-flex items-center gap-2 text-[#1F4E79] hover:text-[#163a5f] font-bold text-sm transition-colors duration-200"
          data-testid={`${testId}-doc-link`}
        >
          <ExternalLink size={15} />
          {docLabel}</Link>
      </div>
    </div>
  </div>
);

/* ─────────────────────────────────────── FEATURE CHIP ─────────────────────────────────────── */
const FeatureCard = ({ icon: Icon, title, description, testId }) => (
  <div
    className="rounded-xl bg-white/95 backdrop-blur-sm border border-white/50 hover:border-[#1F4E79]/40 hover:shadow-lg p-5 transition-all duration-200"
    data-testid={testId}
  >
    <div className="flex items-start gap-3.5">
      <div className="flex-shrink-0 mt-0.5 w-10 h-10 rounded-lg bg-[#1F4E79]/10 flex items-center justify-center">
        <Icon size={20} className="text-[#1F4E79]" strokeWidth={1.75} />
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="text-[15px] font-bold text-gray-900 leading-snug mb-1.5">{title}</h4>
        <p className="text-[13px] text-gray-700 leading-relaxed">{description}</p>
      </div>
    </div>
  </div>
);

/* ─────────────────────────────────────── PAGE ─────────────────────────────────────── */
const ProductsEcommerce = () => {
  const navigate = useNavigate();
  const videoRef = useRef(null);

  useEffect(() => {
    const v = videoRef.current;
    if (v) {
      v.muted = true;
      v.playsInline = true;
      const attempt = v.play();
      if (attempt && typeof attempt.catch === 'function') attempt.catch(() => {});
    }
  }, []);

  return (
    <ProductPageLayout
      currentId="ecommerce-solutions"
      customHero={<EcommerceHero />}
      bodyBgClassName=""
      mainTestId="products-ecommerce-page"
    >
      {/* Looping video bleeds across the entire body */}
      <video
        ref={videoRef}
        src={HERO_VIDEO}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        className="fixed inset-0 -z-10 w-full h-full object-cover"
        aria-hidden="true"
        data-testid="ecommerce-body-video"
      />
      <div className="fixed inset-0 -z-10 bg-gradient-to-b from-[#0a1220]/55 via-[#0a1220]/65 to-[#0a1220]/72" aria-hidden="true" />

      {/* Intro */}
      <div className="text-center mb-12 mt-2" data-testid="ecommerce-intro">
        <p className="text-[11px] font-bold text-[#ffcba6] uppercase tracking-[0.2em] mb-3">Online Payment Suite</p>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight tracking-tight mb-5" style={{ textShadow: '0 3px 14px rgba(0,0,0,0.55)' }}>
          Scale With Your Business
        </h2>
        <p className="text-lg text-white/95 leading-relaxed max-w-3xl mx-auto font-medium" style={{ textShadow: '0 2px 10px rgba(0,0,0,0.55)' }}>
          eCOMM provides comprehensive online payment options that scale with your business. Whether you're building a custom ecommerce platform or need quick integration, our two core methods offer the perfect balance of flexibility, security and ease of implementation.
        </p>
      </div>

      {/* Choose Your Integration Approach — side by side */}
      <div className="mb-14" data-testid="integration-approach">
        <div className="text-center mb-8">
          <p className="text-[11px] font-bold text-[#ffcba6] uppercase tracking-[0.2em] mb-2">Two Paths, One Platform</p>
          <h2 className="text-3xl sm:text-[2.25rem] font-bold text-white leading-tight tracking-tight" style={{ textShadow: '0 3px 14px rgba(0,0,0,0.55)' }}>
            Choose Your Integration Approach
          </h2>
          <div className="h-1 w-20 bg-[#ef5b25] rounded-full mt-4 mx-auto" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <IntegrationCard
            testId="integration-api"
            eyebrow="Maximum Control"
            title="Direct API Integration"
            subtitle="Complete control and customisation for your online checkout."
            intro="Build and manage your own fully customised payment experience using eCOMM's flexible Direct API integration. Our API gives merchants complete control over the checkout journey, branding and customer experience."
            listTitle="What You Get"
            items={[
              <><strong>Full Design Control</strong> — merchants build and control their own payment website and checkout page</>,
              <><strong>Brand Consistency</strong> — complete customisation to match your brand identity</>,
              <><strong>Style Guide Support</strong> — comprehensive branding and presentation guidance</>,
              <><strong>Flexible Implementation</strong> — integrate exactly how your business needs</>,
            ]}
            bestFor="Established Online Businesses With Development Resources Seeking Maximum Control Over The Customer Payment Journey."
            docLink="/technical-documentation/api-reference"
            docState={{ from: '/products/ecommerce-solutions' }}
            docLabel="API Reference Docs"
            accent="from-[#0a1929] to-[#163a5f]"
            icon={Code2}
          />

          <IntegrationCard
            testId="integration-hpp"
            eyebrow="Rapid & Secure"
            title="Hosted Payment Page"
            subtitle="Secure, compliant payment processing with minimal development."
            intro="Simplify online payment acceptance with eCOMM's secure hosted payment solutions. Our pre-certified infrastructure allows businesses to launch quickly while maintaining a professional and consistent checkout experience."
            listTitle="Key Benefits"
            items={[
              <><strong>Option 1: Hosted Payment Page</strong> — redirect customers to our secure payment environment</>,
              <><strong>Option 2: iFrame Integration</strong> — embed our payment form seamlessly within your checkout</>,
              <><strong>No PCI Compliance Burden</strong> — eCOMM assumes full responsibility for certification</>,
              <><strong>Rapid Implementation</strong> — start accepting payments with minimal development effort</>,
              <><strong>Enterprise Grade Security</strong> — secure and reliable payment infrastructure</>,
            ]}
            bestFor="Growing Businesses Wanting Secure Payment Processing Without Extensive Compliance Overhead."
            docLink="/technical-documentation/api-reference/hosted-payment-page"
            docState={{ from: '/products/ecommerce-solutions' }}
            docLabel="Hosted Payment Page Docs"
            accent="from-[#1F4E79] to-[#0E88A3]"
            icon={ShieldCheck}
          />
        </div>
      </div>

      {/* Advanced eCommerce Features */}
      <div id="advanced-features" className="mb-12 scroll-mt-28" data-testid="advanced-features">
        <div className="mb-8">
          <p className="text-[11px] font-bold text-[#ffcba6] uppercase tracking-[0.2em] mb-2">Beyond The Basics</p>
          <h2 className="text-3xl sm:text-[2.25rem] font-bold text-white leading-tight tracking-tight" style={{ textShadow: '0 3px 14px rgba(0,0,0,0.55)' }}>
            Advanced eCommerce Features
          </h2>
          <div className="h-1 w-20 bg-[#ef5b25] rounded-full mt-4" />
          <p className="text-white/95 mt-5 leading-relaxed max-w-3xl" style={{ textShadow: '0 2px 10px rgba(0,0,0,0.55)' }}>
            Enhance your online payment experience with a powerful suite of additional eCommerce features designed to support flexible payment acceptance, improve customer convenience and streamline day-to-day operations. eCOMM provides the tools businesses need to manage transactions efficiently across multiple channels.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <FeatureCard
            testId="feature-pay-by-link"
            icon={LinkIcon}
            title="Pay By Link"
            description="Generate secure payment links instantly via SMS, email or social media for fast and convenient remote payments. Perfect for businesses without websites."
          />
          <FeatureCard
            testId="feature-virtual-terminal"
            icon={Monitor}
            title="Virtual Terminal"
            description="Accept secure card payments directly through a browser based virtual terminal — ideal for phone orders and back-office processing."
          />
          <FeatureCard
            testId="feature-moto"
            icon={Phone}
            title="MOTO (Mail/Telephone Orders)"
            description="Securely process mail and telephone orders through a PCI-compliant payment solution designed for traditional order taking."
          />
          <FeatureCard
            testId="feature-instant-remote"
            icon={Send}
            title="Instant Remote Payment"
            description="Send secure payment links during customer calls to eliminate verbal card sharing and improve payment security."
          />
          <FeatureCard
            testId="feature-recurring"
            icon={RotateCw}
            title="Recurring Billing"
            description="Automate recurring payments for subscriptions, instalments and ongoing customer billing arrangements."
          />
          <FeatureCard
            testId="feature-tokenisation"
            icon={Lock}
            title="Tokenisation"
            description="Replace sensitive card data with secure tokens to improve security and significantly reduce PCI exposure."
          />
        </div>
      </div>

      {/* Final CTA strip */}
      <div
        className="rounded-xl bg-gradient-to-br from-[#0a1929] to-[#163a5f] p-8 sm:p-12 text-center shadow-lg mb-2"
        data-testid="ecommerce-final-cta"
      >
        <h3 className="text-3xl sm:text-4xl font-bold text-white leading-tight tracking-tight mb-4">
          Ready To Accept Payments Online?
        </h3>
        <p className="text-white/90 text-base sm:text-lg leading-relaxed max-w-2xl mx-auto mb-8">
          Tell us about your business and we'll match you with the right integration approach.
        </p>
        <Link
          to="/get-started-ecommerce"
          onClick={() => window.__resetSourceScroll?.()}
          className="group inline-flex items-center gap-2.5 px-7 py-3.5 bg-white text-[#1F4E79] font-bold rounded-lg hover:bg-white/90 hover:gap-3.5 transition-all duration-200 shadow-md hover:shadow-lg"
          data-testid="ecommerce-final-cta-button"
        >
          Get Started Here</Link>
      </div>
    </ProductPageLayout>
  );
};

export default ProductsEcommerce;
